import { Link, useParams, useSearchParams } from "react-router-dom";
import { Badge } from "@/uiCore";
import { Alert, Stack } from "@altimateai/lego";
import { Learnings, TaskLabels, TeamMatesConfig } from "@altimateai/ui-components/teammate";
import useComponentsHelper from "@/modules/app/useComponentsHelper";
import { Button } from "@altimateai/lego";
import { ArrowLeftIcon } from "@/assets/icons";
import classes from "./teammate.module.scss";
import { Typography } from "@altimateai/lego";
import { ChatbotUrls, ContextOption } from "@altimateai/ui-components/chatbotV2";
import { API_URL } from "@/config";
import useNotifications from "../settings/components/notifications/useNotifications";
import useAppActions from "@/modules/app/useAppActions";
import useAppContext from "@/modules/app/useAppContext";
import { useBreadcrumb } from "@/components/breadcrumbs";
const Teammate = () => {
  const { isComponentsApiInitialized } = useComponentsHelper();

  const { teammate } = useParams();
  useBreadcrumb(teammate ?? "Details", {
    defaultParents: [{ label: "Teammates", pathname: "/teammates" }],
  });
  const [searchParams] = useSearchParams();
  const learning = searchParams.get("learning");
  const teammateConfig = TeamMatesConfig.find(t => t.key === teammate);
  const { data: notifications } = useNotifications(teammateConfig?.key !== TaskLabels.AlertManager);
  const { dispatch } = useAppContext();
  const { getCommonHeaders } = useAppActions(dispatch);

  const headers = getCommonHeaders();
  if (!isComponentsApiInitialized) {
    return null;
  }

  if (!teammateConfig) {
    return <Alert variant="destructive">Unable to find teammate details.</Alert>;
  }

  const urls =
    teammateConfig.key === TaskLabels.AlertManager
      ? ({
          origin: API_URL,
          askPath: "/alerts/agent/ask",
          proceedPath: "/alerts/agent/proceed",
        } as ChatbotUrls)
      : {};

  const contextOptions: ContextOption[] =
    teammateConfig.key === TaskLabels.AlertManager
      ? [
          {
            label: "Channels",
            key: "notifications",
            type: "submenu" as const,
            options:
              notifications?.notifications.map(notification => ({
                id: notification.id,
                label: `${notification.config.name} (${notification.type})`,
              })) || [],
          },
        ]
      : [];
  return (
    <div className={classes.teammate}>
      <div>
        <Button className="" variant="ghost" asChild>
          <Link to="/teammates">
            <ArrowLeftIcon />
            Back to Teammates
          </Link>
        </Button>
      </div>
      <Stack className="my-2">
        <img src={teammateConfig.avatar} alt={`${teammate}`} />
        <div className="al-flex-1">
          <Stack className="al-items-center">
            <Typography variant="h3" className="mb-0">
              {teammateConfig.name}
            </Typography>
            {teammateConfig.availability.map(availability => (
              <Badge
                color="success"
                key={availability}
                tooltip={`Available in ${availability}`}
                className={availability}
              >
                Available in {availability}
              </Badge>
            ))}
          </Stack>
          <p>{teammateConfig.description}</p>
        </div>
      </Stack>
      <div className="mt-4">
        {teammateConfig.displayComponent ? (
          <teammateConfig.displayComponent
            urls={urls}
            requestParams={{
              headers,
            }}
            contextOptions={contextOptions}
          />
        ) : (
          <div>
            <Typography variant="section-heading">Learnings</Typography>
            <p>
              These are learnings the AI teammate has gathered based on the coaching provided by
              users.
            </p>
            <Learnings
              filters={{ taskLabel: teammate as keyof typeof TaskLabels }}
              learning={learning}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Teammate;
